import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Repeat } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import api, { formatApiErrorDetail } from "../../lib/api";
import { MonthGrid, shiftMonth, monthLabel } from "./MonthGrid";

const REC = [["none", "One-time"], ["weekly", "Every week"], ["biweekly", "Every other week"], ["monthly", "Every month"]];
const field = "w-full bg-brand-surface border border-brand-border rounded-md px-3 py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary";

function EventDialog({ ev, location, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ title: "", description: "", date: "", start_time: "", end_time: "", recurrence: "none", until: "", ...ev, location }));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      const body = { ...f, until: f.until || null };
      if (ev?.id) await api.put(`/programming/events/${ev.id}`, body); else await api.post("/programming/events", body);
      toast.success("Event saved"); onSaved();
    } catch (e) { toast.error("Couldn't save event", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };
  const del = async () => {
    if (!window.confirm(`Delete "${ev.title}"${ev.recurrence !== "none" ? " and all its repeats" : ""}?`)) return;
    await api.delete(`/programming/events/${ev.id}`); toast.success("Event deleted"); onSaved();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-brand-page" data-testid="prog-event-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl font-normal">{ev?.id ? "Edit event" : "New event"} · {location}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input value={f.title} onChange={set("title")} placeholder="Event title (e.g. Wine Wednesday)" data-testid="prog-title" className={field} />
          <textarea value={f.description} onChange={set("description")} rows={2} placeholder="Short description for the newsletter" data-testid="prog-description" className={`${field} resize-none`} />
          <div className="grid grid-cols-3 gap-2">
            <label className="text-xs text-brand-muted">Date<input type="date" value={f.date} onChange={set("date")} data-testid="prog-date" className={`${field} mt-1`} /></label>
            <label className="text-xs text-brand-muted">Starts<input type="time" value={f.start_time} onChange={set("start_time")} data-testid="prog-start" className={`${field} mt-1`} /></label>
            <label className="text-xs text-brand-muted">Ends<input type="time" value={f.end_time} onChange={set("end_time")} data-testid="prog-end" className={`${field} mt-1`} /></label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs text-brand-muted">Repeats
              <select value={f.recurrence} onChange={set("recurrence")} data-testid="prog-recurrence" className={`${field} mt-1`}>{REC.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
            {f.recurrence !== "none" && <label className="text-xs text-brand-muted">Until (optional)<input type="date" value={f.until || ""} onChange={set("until")} data-testid="prog-until" className={`${field} mt-1`} /></label>}
          </div>
        </div>
        <div className="flex items-center justify-between pt-2">
          {ev?.id ? <button onClick={del} data-testid="prog-delete" className="text-sm text-brand-tertiary hover:text-brand-neg">Delete</button> : <span />}
          <button onClick={save} disabled={!f.title.trim() || !f.date} data-testid="prog-save" className="rounded-md bg-brand-ink text-brand-page px-4 py-2 text-sm hover:bg-brand-primary disabled:opacity-50">Save event</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ProgrammingCalendar({ location }) {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => {
    api.get("/programming/events", { params: { location, month } }).then(({ data: d }) => setData(d)).catch(() => setData({ events: [], occurrences: [] }));
  }, [location, month]);
  useEffect(() => { load(); }, [load]);
  const byDay = {};
  (data?.occurrences || []).forEach((o) => { (byDay[o.on] = byDay[o.on] || []).push(o); });
  const open = (o) => setEditing(data.events.find((e) => e.id === o.id));

  return (
    <div data-testid="prog-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth(shiftMonth(month, -1))} data-testid="prog-prev" className="p-1.5 rounded hover:bg-brand-hover"><ChevronLeft className="h-4 w-4" /></button>
          <p className="font-serif text-2xl text-brand-ink min-w-[180px] text-center" data-testid="prog-month">{monthLabel(month)}</p>
          <button onClick={() => setMonth(shiftMonth(month, 1))} data-testid="prog-next" className="p-1.5 rounded hover:bg-brand-hover"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <button onClick={() => setEditing({ date: `${month}-01` })} data-testid="prog-add" className="inline-flex items-center gap-1.5 rounded-md bg-brand-ink text-brand-page px-4 py-2 text-sm hover:bg-brand-primary"><Plus className="h-4 w-4" /> Add event</button>
      </div>
      <MonthGrid month={month} onDayClick={(day) => setEditing({ date: day })} renderDay={(day) => (byDay[day] || []).map((o) => (
        <button key={`${o.id}-${day}`} onClick={(e) => { e.stopPropagation(); open(o); }} data-testid={`prog-occ-${o.id}-${day}`}
          className="w-full text-left text-[11px] leading-tight rounded px-1.5 py-1 bg-brand-primary/10 text-brand-ink hover:bg-brand-primary/20 truncate">
          {o.recurrence !== "none" && <Repeat className="inline h-2.5 w-2.5 mr-0.5 text-brand-tertiary" />}{o.start_time ? <span className="text-brand-tertiary">{o.timeframe.split(" – ")[0]} </span> : null}{o.title}
        </button>
      ))} />
      <p className="text-xs text-brand-tertiary mt-3">One-time events fill the EVENT slots in newsletter templates. Repeating perks (like Bagel Tuesdays) show here for planning.</p>
      {editing && <EventDialog ev={editing} location={location} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
